import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Search, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState, PageLoading, PageHeader, fmtDate } from "@/components/shared";
import { ComplianceBadge } from "@/components/ComplianceBadge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api from "@/lib/api";

export default function AdminEmployees() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [bulkDel, setBulkDel] = useState(false);
  const load = () => api.get(`/admin/employees?q=${encodeURIComponent(q)}`).then((r) => setRows(r.data.employees));
  useEffect(() => { const timer = setTimeout(load, 200); return () => clearTimeout(timer); }, [q]);

  const toggleSel = (id) => setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const allSelected = rows && rows.length > 0 && sel.length === rows.length;
  const toggleAll = () => setSel(allSelected ? [] : (rows || []).map((r) => r.id));
  const clearSel = () => setSel([]);
  const doBulkDelete = async () => {
    try { const r = await api.post(`/admin/employees/bulk-delete`, { ids: sel }); toast.success(t("adm.emDeleted", { count: r.data.deleted })); setBulkDel(false); clearSel(); load(); }
    catch { toast.error("Error"); }
  };

  return (
    <div data-testid="admin-employees">
      <PageHeader title={t("adm.emTitle")} subtitle={t("adm.emSubtitle")} />
      <div className="relative mb-4 max-w-md"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("adm.emSearch")} className="pl-9" data-testid="admin-employees-search" /></div>

      {sel.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm font-semibold text-navy" data-testid="bulk-count">{t("adm.bulkSelected", { count: sel.length })}</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-full border-red-200 bg-white text-red-600 hover:bg-red-50" onClick={() => setBulkDel(true)} data-testid="bulk-delete"><Trash2 className="mr-1 h-3.5 w-3.5" /> {t("adm.emDelete")}</Button>
            <button onClick={clearSel} className="rounded-lg p-1.5 hover:bg-white" data-testid="bulk-clear" title={t("det.cancel")}><X className="h-4 w-4 text-slate-500" /></button>
          </div>
        </div>
      )}

      {rows === null ? <PageLoading /> : rows.length === 0 ? <EmptyState icon={Search} title={t("adm.emEmpty")} /> : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-employees-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-3"><Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="select all" data-testid="select-all-employees" /></th>
                <th className="px-4 py-3">{t("tbl.colEmployee")}</th><th className="px-4 py-3">{t("adm.coCompany")}</th><th className="px-4 py-3">{t("tbl.colNationality")}</th><th className="px-4 py-3">{t("tbl.colPermit")}</th><th className="px-4 py-3">{t("tbl.colCompliance")}</th><th className="px-4 py-3">{t("tbl.colPortal")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className={`border-b border-slate-50 hover:bg-slate-50 ${sel.includes(e.id) ? "bg-cyan-50/50" : ""}`}>
                  <td className="px-4 py-3"><Checkbox checked={sel.includes(e.id)} onCheckedChange={() => toggleSel(e.id)} aria-label="select" data-testid={`select-employee-${e.id}`} /></td>
                  <td className="px-4 py-3 font-semibold text-navy">{e.first_name} {e.last_name}{e.worker_id && <div className="text-[11px] font-normal text-slate-400">Worker ID: {e.worker_id}</div>}</td>
                  <td className="px-4 py-3 text-slate-600">{e.company}</td>
                  <td className="px-4 py-3 text-slate-600">{e.nationality || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(e.permit_valid_until)}</td>
                  <td className="px-4 py-3"><ComplianceBadge status={e.compliance_status} /></td>
                  <td className="px-4 py-3 text-slate-600">{e.portal_status?.replace("_", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={bulkDel} onOpenChange={setBulkDel}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>{t("adm.emDelete")}</AlertDialogTitle><AlertDialogDescription>{t("adm.emBulkDeleteConfirm", { count: sel.length })}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>{t("det.cancel")}</AlertDialogCancel><AlertDialogAction onClick={doBulkDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-bulk-delete">{t("adm.emDelete")}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
